'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AlertBadge from '@alltrails/alerts/components/AlertBadge/AlertBadge';
import { ALERT_TYPE_ORDER } from '@alltrails/alerts/constants';
import { alertTypeIcon, alertTypeIconColor } from '@alltrails/alerts/utils/alertTypeDisplay';
import { alertTypeLabel } from '@alltrails/alerts/utils/alertTypeLabel';
import Typography from '@alltrails/core/components/Typography';
import type { IconComponent } from '@alltrails/denali/components/Icon';
import Link from '@alltrails/denali/components/Link';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import CautionFilled from '@alltrails/denali/icons/CautionFilled';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import ClosureFilled from '@alltrails/denali/icons/ClosureFilled';
import ErrorFilled from '@alltrails/denali/icons/ErrorFilled';
import InfoFilled from '@alltrails/denali/icons/InfoFilled';
import type { Group } from '@alltrails/groups';
import { type Alert, AlertStatus, AlertType } from '@alltrails/types/Alert';
import type TrailDetails from '@alltrails/types/TrailDetails';
import type TrailInfoSummary from '@alltrails/types/TrailInfoSummary';
import { MOBILE_TOP_ALERT_TYPES } from '../../utils/constants';
import styles from './HighPriorityAlerts.module.scss';

// https://www.figma.com/design/O0IQnhTIdAc8sbNjbEKDpa/Trail-Page-Web-2026?node-id=91-98518&m=dev
const AlertsModal = dynamic(() => import('@alltrails/alerts/components/AlertsModal/AlertsModal'), { ssr: false });

type Props = {
  alerts: Alert[];
  isAdmin?: boolean;
  trailDetails?: TrailDetails;
  trailInfo?: TrailInfoSummary;
  userGroups?: Group[];
  isPLPUser?: boolean;
  variant?: 'banner' | 'inHeader';
  hideOnMobile?: boolean;
};

const alertTypeClass: Record<AlertType, string> = {
  [AlertType.CLOSURE]: styles.error,
  [AlertType.DANGER]: styles.error,
  [AlertType.WARNING]: styles.caution,
  [AlertType.INFORMATION]: styles.info
};

const alertTypeIconFilled: Record<AlertType, IconComponent> = {
  [AlertType.CLOSURE]: ClosureFilled,
  [AlertType.DANGER]: ErrorFilled,
  [AlertType.WARNING]: CautionFilled,
  [AlertType.INFORMATION]: InfoFilled
};

export default function HighPriorityAlerts({
  alerts,
  isAdmin,
  trailDetails,
  trailInfo,
  isPLPUser,
  userGroups,
  variant = 'banner',
  hideOnMobile = false
}: Props) {
  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);
  const isMobile = useIsMobileSizedScreen();
  const intl = useIntl();
  const activeAlerts = alerts.filter(alert => alert.status === AlertStatus.ACTIVE);
  const isCompact = variant === 'inHeader';

  // The banner variant on mobile only shows top-priority types (Closure/Danger);
  // the compact in-header variant shows every active type so Info/Warning aren't dropped. (compact is behind 'web-disco-rm-highlights-on-trail-page' experiment)
  const displayAlerts = !isCompact && isMobile ? activeAlerts.filter(a => MOBILE_TOP_ALERT_TYPES.has(a.alertType)) : activeAlerts;

  if (displayAlerts.length === 0) {
    return null;
  }

  const primaryType = ALERT_TYPE_ORDER.find(type => displayAlerts.some(a => a.alertType === type));

  if (!primaryType) {
    return null;
  }

  const colorVar = alertTypeIconColor[primaryType];
  const IconComponent = alertTypeIcon[primaryType];

  if (variant === 'inHeader') {
    if (!isMobile) {
      return null;
    }
    const FilledIconComponent = alertTypeIconFilled[primaryType];
    return (
      <>
        <button className={classNames(styles.compactAlerts, alertTypeClass[primaryType])} onClick={() => setIsAlertsModalOpen(true)}>
          <FilledIconComponent color={colorVar} size="sm" />
          <span className={styles.compactLabel}>{alertTypeLabel(primaryType, intl)}</span>
          <span className={styles.compactCount}>
            <FormattedMessage defaultMessage="{count, plural, one {# alert} other {# alerts}}" values={{ count: activeAlerts.length }} />
          </span>
        </button>
        {isAlertsModalOpen && trailDetails && (
          <AlertsModal
            isAdmin={isAdmin}
            isPLPUser={isPLPUser}
            onClose={() => setIsAlertsModalOpen(false)}
            trailDetails={trailDetails}
            trailInfo={trailInfo}
            userGroups={userGroups}
          />
        )}
      </>
    );
  }

  if (isMobile && hideOnMobile) {
    return null;
  }

  return (
    <>
      {!isMobile ? (
        <div className={classNames(styles.inlineAlerts, alertTypeClass[primaryType])}>
          <AlertBadge alertType={primaryType} size="md" />
          <Link className={styles.inlineAlertsLink} onClick={() => setIsAlertsModalOpen(true)}>
            <FormattedMessage defaultMessage="{count, plural, one {# alert} other {# alerts}}" values={{ count: activeAlerts.length }} />
          </Link>
          <div className={styles.dividerDot}>·</div>
        </div>
      ) : (
        <button className={classNames(styles.alerts, alertTypeClass[primaryType])} onClick={() => setIsAlertsModalOpen(true)}>
          <div className={styles.icon}>
            <IconComponent color={colorVar} />
          </div>
          <div className={styles.text}>
            <Typography className={styles.link} component="div" variant="text200">
              {alertTypeLabel(primaryType, intl)}
            </Typography>
            {activeAlerts.length > 1 && (
              <div className={styles.sub}>
                <FormattedMessage defaultMessage="{count, plural, one {# alert} other {# alerts}}" values={{ count: activeAlerts.length }} />
              </div>
            )}
          </div>
          <ChevronSm className={styles.chevron} orientation="right" />
        </button>
      )}
      {isAlertsModalOpen && trailDetails && (
        <AlertsModal
          isAdmin={isAdmin}
          isPLPUser={isPLPUser}
          onClose={() => setIsAlertsModalOpen(false)}
          trailDetails={trailDetails}
          trailInfo={trailInfo}
          userGroups={userGroups}
        />
      )}
    </>
  );
}
